import { Paragraph } from "@/model";
import * as ParagraphData from "@/model/paragraph";
import { isValidJSON, isUrl, isBlobUrl } from "@/util/stringUtil";
import { isEmpty } from "@/util/typeUtil";
import {
    isApi,
    isCarousel,
    isCode,
    isCustom,
    isFile,
    isList,
    isLLMAnswer,
    isMedia,
    isNext,
    isNextConditionData,
    isParagraphButtonData,
    isQuickReply,
    isRaw,
    isTemplate,
    isTextInput,
    isTextOutput,
    isVariable
} from "./typeGuards";


const VARIABLE_FORMAT_REGEXP = /\{\{\s*(?!\s*\}\})[\s\S]+?\}\}/;
export const isVariableFormat = (value: string): boolean => {
    return VARIABLE_FORMAT_REGEXP.test(value);
};
export const isValidParagraphUrl = (url: string): boolean => {
    if (!url.trim()) {
        return false;
    }
    if (isUrl(url) || isBlobUrl(url)) {
        return true;
    }
    return isVariableFormat(url);
};

const isValidTextPayload = (payload: string): boolean => {
    if (payload.startsWith("{") && payload.includes("text:")) {
        return true;
    }
    const [tag, value] = payload?.split(":") ?? [];
    return tag === "text" && !!value?.trim();
};

const isValidScenarioPayload = (payload: string): boolean => {
    const [tag, scenarioId, sendQuery] = payload?.split(":") ?? [];
    return tag === "act_scenario" && !!scenarioId?.trim() && sendQuery !== undefined;
};

const isValidParagraphButton = (button: ParagraphData.ParagraphButtonData): boolean => {
    if (!button.label || !button.button_type) {
        return false;
    }
    if (button.button_type === "text") {
        return isValidTextPayload(button.payload ?? "");
    }
    if (button.button_type === "scenario") {
        return isValidScenarioPayload(button.payload ?? "");
    }
    if (button.button_type === "next") {
        if (!button.next) {
            return false;
        }
        if (button.options && !isValidVariableData(button.options)) {
            return false;
        }
        return true;
    }
    if (button.button_type === "link") {
        return isValidParagraphUrl(button.link_mobile ?? "");
    }
    return true;
};

const isValidNextConditionData = (next: ParagraphData.NextConditionData): boolean => {
    return !!next.condition && !!next.next;
};

export const isValidMediaData = (data: ParagraphData.MediaData): boolean => {
    return !!data.media_type && !!data.media_url;
};

const isValidCaraouselData = (data: ParagraphData.ParagraphOutputCarousel['data']): boolean => {
    if (!data || data.length === 0) {
        return false;
    }
    return data.every(card => {
        if (!card.title && !card.text && !isValidMediaData(card)) {
            return false;
        }
        switch (card.click_type) {
            case "link":
                return isValidParagraphUrl(card.link_mobile ?? "");
            case "text":
                return !!(card.payload?.split(":")[1]?.trim());
            case "next":
                return !!card.next;
            case "none":
            default:
                return card.buttons ? card.buttons.every(isValidParagraphButton) : true;
        }
    });
};

const isValidVariableData = (data: ParagraphData.VariableData): boolean => {
    if (data.global && !Object.entries(data.global).every(([key, value]) => key && value !== undefined)) {
        return false;
    }
    if (data.local && !Object.entries(data.local).every(([key, value]) => key && value !== undefined)) {
        return false;
    }
    return true;
};

export const isValidParagraphData = (p: Paragraph): boolean => {
    try {
        if (isTextOutput(p)) {
            if (Array.isArray(p.data)) {
                return p.data.length > 0 && p.data.every(item => !!item.text);
            }
            if (typeof p.data === "string") {
                return !!p.data;
            }
            return !!p.data.text;
        }
        if (isMedia(p)) {
            return isValidMediaData(p.data);
        }
        if (isFile(p)) {
            return !!p.data.file_url && !!p.data.text;
        }
        if (isCarousel(p)) {
            return isValidCaraouselData(p.data);
        }
        if (isLLMAnswer(p)) {
            if (!p.data.text) {
                return false;
            }
            if (p.data.references?.length && !p.data.references.every(item => !!item.label)) {
                return false;
            }
            if (p.data.suggestions?.length && !p.data.suggestions.every(item => !!item.label)) {
                return false;
            }
            return true;
        }
        if (isCustom(p) || isRaw(p)) {
            return true;
        }
        if (isVariable(p)) {
            if (isEmpty(p.data.global) && isEmpty(p.data.local)) {
                return false;
            }
            return isValidVariableData(p.data);
        }
        if (isApi(p)) {
            const { url, method, headers, params, body } = p.data;
            if (
                !isValidParagraphUrl(url)
                    || !method
                    || (headers && !Object.entries(headers).every(([key, value]) => key && value))
                    || (params && !Object.entries(params).every(([key, value]) => key && value))
            ) {
                return false;
            }
            return !body || isValidJSON(body);
        }
        if (isCode(p)) {
            return !!p.data.code;
        }
        if (isList(p) || isQuickReply(p)) {
            return p.data.every(item => {
                return (
                    isParagraphButtonData(item) && isValidParagraphButton(item))
                    || (isNextConditionData(item) && isValidNextConditionData(item)
                );
            });
        }
        if (isTextInput(p)) {
            return !p.data || p.data.every(isValidNextConditionData);
        }
        if (isNext(p)) {
            return p.data.every(isValidNextConditionData);
        }
        if (isTemplate(p)) {
            return !!p.data.template_id && !!p.data.template_name;
        }
        return false;
    } catch {
        return false;
    }
};

